import type { TUser } from "@/app/(auth)/_utils/functions/getUser.ts";
import type { IOrganizationDocument } from "@/app/_utils/database/db-models/organization.ts";


/**
 * Demo org used when presentation mode is on. Override with
 * `NEXT_PUBLIC_PRESENTATION_ORGANIZATION_ID`.
 */
export const PRESENTATION_ORGANIZATION_ID = "657c2e4880d92ac3255d28c2";

/**
 * Only this project is visible / reachable while presentation mode is on. Override with
 * `NEXT_PUBLIC_PRESENTATION_PROJECT_ID`.
 */
export const PRESENTATION_PROJECT_ID = "69d639deb11d2d79042f027d";

/** Cognito ID token / user attribute key — source of truth for presentation mode. */
export const COGNITO_PRESENTATION_MODE_ATTRIBUTE =
  "custom:presentation_mode" as const;

/**
 * A job that only belongs to the presentation project was created inside
 * the demo environment and should not have its chemistry obfuscated.
 *
 * @param jobProjects - The `projects` array from a job document.
 *
 * @returns True when the job is exempt from presentation-mode blurring.
 */
export function isJobExemptFromPresentation(
  jobProjects: string[] | undefined,
): boolean {
  if (!jobProjects) {
    return false;
  }

  return (
    jobProjects.length === 1 && jobProjects[0] === PRESENTATION_PROJECT_ID
  );
}

const OBJECT_ID_PATH = /^\/([a-f0-9]{24})(\/|$)/i;

/**
 * Parses Cognito `custom:presentation_mode` (or equivalent) as a boolean flag.
 *
 * @param value - Raw attribute value from the ID token.
 *
 * @returns True when the user has presentation mode enabled in Cognito.
 */
export function isPresentationModeAttributeEnabled(value: unknown): boolean {
  if (typeof value !== "string" || value === "") {
    return false;
  }

  const v = value.toLowerCase().trim();

  return v === "true" || v === "1" || v === "yes";
}

/**
 * Whether the path is allowed while presentation mode is active.
 * Home (`/` — project table for the active org) and profile stay available. `/organization`
 * (org management) is blocked. Other routes must be under the demo project.
 *
 * @param pathname - Next.js pathname (e.g. `/` or `/[project-id]/...`).
 *
 * @returns True when navigation is allowed in presentation mode.
 */
export function isPathAllowedInPresentationMode(pathname: string): boolean {
  if (pathname === "/" || pathname === "") {
    return true;
  }

  if (pathname === "/profile" || pathname.startsWith("/profile/")) {
    return true;
  }

  if (pathname.startsWith("/structures/")) {
    return true;
  }

  const match = OBJECT_ID_PATH.exec(pathname);

  if (match?.[1]) {
    return match[1].toLowerCase() === PRESENTATION_PROJECT_ID.toLowerCase();
  }

  return false;
}

/**
 * Whether any organization in the list is the PhaseTree presentation org.
 *
 * @param orgs - Organizations from the user record.
 *
 * @returns True when the presentation org id is present.
 */
function orgListIncludesPresentationOrg(
  orgs: Partial<IOrganizationDocument>[] | undefined,
): boolean {
  return (
    orgs?.some(
      (org) =>
        typeof org._id === "string" &&
        org._id.toLowerCase() === PRESENTATION_ORGANIZATION_ID.toLowerCase(),
    ) ?? false
  );
}

/**
 * User may turn on presentation mode only if they belong to the PhaseTree demo org.
 *
 * @param user - Authenticated user (or null).
 *
 * @returns True when the user can enable presentation mode.
 */
export function userHasPresentationOrganizationAccess(
  user: Partial<TUser> | null,
): boolean {
  if (user === null) {
    return false;
  }

  return (
    orgListIncludesPresentationOrg(user.organizations) ||
    orgListIncludesPresentationOrg(user.sharedOrganizations)
  );
}

/**
 * Resolves the PhaseTree demo organization from the user’s org lists.
 *
 * @param user - Authenticated user (or null).
 *
 * @returns The matching org document fragment, if any.
 */
export function findPresentationOrganization(
  user: Partial<TUser> | null,
): Partial<IOrganizationDocument> | undefined {
  if (user === null) {
    return undefined;
  }

  const fromOwned = user.organizations?.find(
    (org) =>
      typeof org._id === "string" &&
      org._id.toLowerCase() === PRESENTATION_ORGANIZATION_ID.toLowerCase(),
  );
  if (fromOwned) {
    return fromOwned;
  }

  return user.sharedOrganizations?.find(
    (org) =>
      typeof org._id === "string" &&
      org._id.toLowerCase() === PRESENTATION_ORGANIZATION_ID.toLowerCase(),
  );
}
